import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Phone, Mail, MapPin, Menu, X, ChevronDown, MessageCircle } from "lucide-react";
import { company, navLinks } from "../mock";

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [kurumsalOpen, setKurumsalOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setKurumsalOpen(false);
  }, [location.pathname]);

  const solid = scrolled || mobileOpen;

  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      {/* Topbar */}
      <div
        className={`text-xs transition-all duration-500 overflow-hidden ${
          solid
            ? "max-h-0 opacity-0"
            : "max-h-12 opacity-100 border-b border-white/10"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-11 text-white/60">
          <div className="flex items-center gap-6">
            <a
              href={company.phoneHref}
              className="flex items-center gap-2 hover:text-white transition-colors"
            >
              <Phone size={13} className="text-[#1e9cd7]" />
              <span className="tracking-wide">{company.phone}</span>
            </a>
            <a
              href={company.emailHref}
              className="hidden md:flex items-center gap-2 hover:text-white transition-colors"
            >
              <Mail size={13} className="text-[#1e9cd7]" />
              <span className="tracking-wide">{company.email}</span>
            </a>
          </div>
          <a
            href={company.mapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 hover:text-white transition-colors"
          >
            <MapPin size={13} className="text-[#1e9cd7]" />
            <span className="tracking-wide uppercase text-[11px] font-medium">
              Haritada Göster
            </span>
          </a>
        </div>
      </div>

      {/* Main nav */}
      <div
        className={`transition-all duration-500 ${
          solid
            ? "bg-white/95 backdrop-blur-xl shadow-[0_10px_40px_-12px_rgba(13,43,79,0.28)]"
            : "bg-transparent"
        }`}
      >
        <div
          className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between transition-all duration-500 ${
            solid ? "h-[72px]" : "h-[88px]"
          }`}
        >
          <Link
            to="/"
            className="flex items-center shrink-0"
            aria-label="Nikomedya Turizm Anasayfa"
          >
            <img
              src="/images/logo.webp"
              alt="Nikomedya Turizm"
              className={`h-11 w-auto transition-all duration-500 ${
                solid ? "" : "logo-white"
              }`}
            />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-9">
            {navLinks.map((link) =>
              link.children ? (
                <div key={link.label} className="relative group">
                  <button
                    className={`nav-link flex items-center gap-1.5 text-[14px] font-semibold tracking-wide ${
                      location.pathname.startsWith("/kurumsal")
                        ? "text-[#1e9cd7] active"
                        : solid
                        ? "text-[#0d2b4f] hover:text-[#1e9cd7]"
                        : "text-white/90 hover:text-white"
                    }`}
                  >
                    {link.label}
                    <ChevronDown
                      size={14}
                      className="transition-transform duration-300 group-hover:rotate-180"
                    />
                  </button>
                  <div className="absolute top-full left-0 pt-5 opacity-0 invisible translate-y-2 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-300 z-50">
                    <div className="bg-white rounded-xl shadow-[0_24px_60px_-15px_rgba(13,43,79,0.35)] border border-slate-100 py-2.5 min-w-[240px]">
                      {link.children.map((child) => (
                        <NavLink
                          key={child.path}
                          to={child.path}
                          className={({ isActive }) =>
                            `block px-6 py-3 text-sm font-medium transition-colors ${
                              isActive
                                ? "text-[#1e9cd7] bg-[#e8f5fc]"
                                : "text-[#0d2b4f] hover:text-[#1e9cd7] hover:bg-slate-50"
                            }`
                          }
                        >
                          {child.label}
                        </NavLink>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <NavLink
                  key={link.path}
                  to={link.path}
                  className={({ isActive }) =>
                    `nav-link text-[14px] font-semibold tracking-wide ${
                      isActive
                        ? "text-[#1e9cd7] active"
                        : solid
                        ? "text-[#0d2b4f] hover:text-[#1e9cd7]"
                        : "text-white/90 hover:text-white"
                    }`
                  }
                >
                  {link.label}
                </NavLink>
              )
            )}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href={company.whatsappHref}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-whatsapp hidden sm:inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-bold"
            >
              <MessageCircle size={16} />
              Teklif Al
            </a>
            <button
              className={`lg:hidden p-2 transition-colors ${
                solid ? "text-[#0d2b4f]" : "text-white"
              }`}
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Menü"
            >
              {mobileOpen ? <X size={26} /> : <Menu size={26} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={`lg:hidden bg-white border-t border-slate-100 shadow-2xl transition-all duration-300 overflow-hidden ${
          mobileOpen ? "max-h-[520px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <nav className="px-5 py-4 flex flex-col">
          {navLinks.map((link) =>
            link.children ? (
              <div key={link.label}>
                <button
                  onClick={() => setKurumsalOpen(!kurumsalOpen)}
                  className="w-full flex items-center justify-between py-3.5 text-[#0d2b4f] font-semibold border-b border-slate-100"
                >
                  {link.label}
                  <ChevronDown
                    size={16}
                    className={`transition-transform duration-300 ${
                      kurumsalOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    kurumsalOpen ? "max-h-48" : "max-h-0"
                  }`}
                >
                  {link.children.map((child) => (
                    <Link
                      key={child.path}
                      to={child.path}
                      className="block py-2.5 pl-4 text-sm text-[#5b6b7d] hover:text-[#1e9cd7]"
                    >
                      {child.label}
                    </Link>
                  ))}
                </div>
              </div>
            ) : (
              <Link
                key={link.path}
                to={link.path}
                className="py-3.5 text-[#0d2b4f] font-semibold border-b border-slate-100 hover:text-[#1e9cd7] transition-colors"
              >
                {link.label}
              </Link>
            )
          )}
          <a
            href={company.whatsappHref}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-whatsapp mt-4 mb-2 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm font-bold"
          >
            <MessageCircle size={17} />
            WhatsApp'tan Teklif Al
          </a>
        </nav>
      </div>
    </header>
  );
};

export default Header;
